<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <Rate
      v-bind="rating"
      @rate="setScore"
    >
      <BaseButton
        :text="$t('actions.confirm')"
        size="s"
        slot="actions"
        @click="confirmScore"
      />
    </Rate>
    <ModalConfirm
      v-if="isModalVisible"
      v-bind="modal"
      slot="root"
      class="screen__overlay"
      @agree="goToVideoInstruction"
      @disagree="goToSendVideo"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import screenMixin from '@/mixins/screen';
import ModalConfirm from '@/components/ModalConfirm.vue';
import BaseButton from '@/components/BaseButton.vue';
import Rate from '@/components/Rate.vue';

import CONSTANTS from '@/helpers/constants';

const {
  ACTION_OPEN_MODAL_CONFIRM_VIDEO_RECORDING,
  ACTION_SCORE_BAD,
  ACTION_SCORE_GOOD,
  ACTION_SCORE_PERFECT,
  STEP_MODAL_CONFIRM_VIDEO_RECORDING,
} = CONSTANTS;

const BAD_SCORE = 1;
const GOOD_SCORE = 2;
const PERFECT_SCORE = 3;

export default {
  name: 'RateEventScreen',
  mixins: [screenMixin],
  components: {
    ModalConfirm,
    BaseButton,
    Rate,
  },
  data() {
    return {
      isModalVisible: false,
    };
  },
  computed: {
    ...mapState('feedback', [
      'event',
      'score',
    ]),
    title() {
      return this.$t('titles.rateEvent');
    },
    isErrorVisible() {
      return !this.event;
    },
    error() {
      return {
        title: this.$t('titles.badConnection'),
        text: this.$t('texts.feedbackNotPossible'),
        actions: [
          {
            text: this.$t('actions.home'),
            action: () => this.$router.push(this.$router.options.paths.home),
          },
        ],
      };
    },
    modal() {
      return {
        title: this.$t('titles.confirmVideoRecording'),
        agreeText: this.$t('actions.yesRecordVideo'),
        disagreeText: this.$t('actions.noSkipVideo'),
        replic: STEP_MODAL_CONFIRM_VIDEO_RECORDING,
      };
    },
    rating() {
      return {
        title: this.event?.performance?.title,
        scores: this.scores,
        score: this.score,
      };
    },
    scores() {
      return [
        {
          icon: 'smile_bad',
          name: this.$t('titles.bad'),
          score: BAD_SCORE,
        },
        {
          icon: 'smile_fine',
          name: this.$t('titles.fine'),
          score: GOOD_SCORE,
        },
        {
          icon: 'smile_great',
          name: this.$t('titles.great'),
          score: PERFECT_SCORE,
        },
      ];
    },
  },
  created() {
    this.initActions();
    this.clearVideo();
    this.clearScore();
  },
  methods: {
    ...mapMutations('feedback', [
      'setScore',
      'clearVideo',
      'clearScore',
    ]),
    confirmScore() {
      if (!this.score) return;
      this.showModal();
    },
    showModal() {
      this.isModalVisible = true;
    },
    hideModal() {
      this.isModalVisible = false;
    },
    goToVideoInstruction() {
      this.$router.push(this.$router.options.paths.videoInstruction);
    },
    goToSendVideo() {
      this.$router.push(this.$router.options.paths.sendVideo);
    },
    setBadScore() {
      return this.setScore(BAD_SCORE);
    },
    setGoodScore() {
      return this.setScore(GOOD_SCORE);
    },
    setPerfectScore() {
      return this.setScore(PERFECT_SCORE);
    },
    initActions() {
      const rootActions = [
        [ACTION_OPEN_MODAL_CONFIRM_VIDEO_RECORDING, this.confirmScore],
        [ACTION_SCORE_BAD, this.setBadScore],
        [ACTION_SCORE_GOOD, this.setGoodScore],
        [ACTION_SCORE_PERFECT, this.setPerfectScore],
      ];

      rootActions.forEach((action) => this.$root.$on(...action));

      this.$once('hook:beforeDestroy', () => {
        rootActions.forEach((action) => this.$root.$off(...action));
      });
    },
  },
};
</script>
